import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import ProjectsUpsellSpendingBannerImage from 'assets/images/project_management/banners/projects_upsell_spending_banner.png'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ProjectManagementProjectDetailsPageSpendingPageDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import ProjectSpendingCostsDataCollection from 'modules/project_management/components/tables/ProjectSpendingCostsDataCollection'
import ProjectSpendingDetailPanel from 'modules/project_management/components/tables/ProjectSpendingCostsDataCollection/ProjectSpendingDetailPanel'
import { UpsellProjectSpendingMessage } from 'modules/project_management/components/upsells/UpsellProjectSpendingMessage'
import { ProjectsUpsellRequestSources } from 'modules/project_management/components/upsells/constants'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import { ProjectDetailsPageSpendingPageInsights } from './Insights'
import { IProjectSpendingPanelData } from './types'

const SpendingPage = () => {
  const { id } = useParams()
  const projectId = Number(id)
  const { hasExpenses, hasCost } = useOutletContext<{
    hasExpenses: boolean
    hasCost: boolean
  }>()

  const hasCostAndExpenses = hasCost && hasExpenses

  const [showExpensesBanner, setShowExpensesBanner] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideProjectsExpensesUpsellBanner,
    true
  )

  const { data, refetch } = useQuery(ProjectManagementProjectDetailsPageSpendingPageDocument, {
    variables: { projectId, includeInsights: hasCostAndExpenses },
  })

  const project = data?.projectManagement.projectsConnection.edges[0]?.node

  const shouldShowFullUpsell = !hasCost && !hasExpenses
  const shouldShowSpendingUpsell = !hasExpenses && showExpensesBanner

  useEffect(() => {
    track(trackingEvents.projectManagement.spending.visitSpendingSection)
  }, [])

  const projectLegalEntityCurrency = project?.legalEntity.currency || 'EUR'
  const [showSpendingDetailPanel, setShowSpendingDetailPanel] = useState<boolean>(false)
  const [projectSpendingPanelData, setProjectSpendingPanelData] =
    useState<IProjectSpendingPanelData>({
      effectiveOn: '',
      employeeName: '',
      exchangeRate: null,
      expensableId: 0,
      legalEntityLegalName: null,
      legalEntityAmountCents: null,
      legalEntityAmountCurrency: null,
      originalAmountCents: 0,
      originalAmountCurrency: projectLegalEntityCurrency,
    })

  if (!project) return null

  if (shouldShowFullUpsell) {
    return <UpsellProjectSpendingMessage hasExpenses={hasExpenses} />
  }
  const openSidePanel = (newData: IProjectSpendingPanelData) => {
    setShowSpendingDetailPanel(true)
    setProjectSpendingPanelData(newData)
  }

  const handleExpensesBannerClose = () => {
    setShowExpensesBanner(false)
  }

  return (
    <Box gap="s24">
      <>
        {shouldShowSpendingUpsell && (
          <UpsellingKit.ProductBanner
            moduleRequested={AvailableModules.projects}
            featureRequested={Features.expenses_core}
            requestSource={ProjectsUpsellRequestSources.expenses}
            requestReason={ValidReasons.MissingFeature}
            title={i18n.t('project_management.upselling.expenses_banner.title')}
            subtitle={i18n.t('project_management.upselling.expenses_banner.subtitle')}
            mediaUrl={ProjectsUpsellSpendingBannerImage}
            onClose={handleExpensesBannerClose}
            primaryAction={{
              label: i18n.t('project_management.upselling.cta'),
              variant: 'outline',
            }}
          />
        )}
      </>
      <ProjectDetailsPageSpendingPageInsights
        projectId={project.id}
        projectCurrency={projectLegalEntityCurrency}
      />
      <ProjectSpendingCostsDataCollection
        projectId={project.id}
        projectCurrency={projectLegalEntityCurrency}
        openSidePanel={openSidePanel}
        onCloseSidePanel={refetch}
      />
      <ProjectSpendingDetailPanel
        onClose={() => setShowSpendingDetailPanel(false)}
        projectSpendingPanelData={projectSpendingPanelData}
        showSpendingDetailPanel={showSpendingDetailPanel}
      />
    </Box>
  )
}

export default SpendingPage
